export const ONBOARDING_TOOLTIP_WIDTH = 320;
export const ONBOARDING_TOOLTIP_HEIGHT = 280;
export const ONBOARDING_FIELD_TOOLTIP_HEIGHT = 220;

const VIEWPORT_PADDING = 16;
const TOOLTIP_GAP = 12;
const COMPACT_VIEWPORT_MAX_WIDTH = 640;

export function isCompactViewport(): boolean {
  return window.innerWidth < COMPACT_VIEWPORT_MAX_WIDTH;
}

export function getTooltipWidth(): number {
  return Math.min(ONBOARDING_TOOLTIP_WIDTH, window.innerWidth - VIEWPORT_PADDING * 2);
}
export interface TooltipPosition {
  top: number;
  left: number;
}

export type TooltipPlacementPreference = 'default' | 'modal';

export interface ComputeTooltipPositionOptions {
  placementPreference?: TooltipPlacementPreference;
}

type PlacementSide = 'bottom' | 'right' | 'left' | 'top';

function clampHorizontal(left: number, tooltipWidth: number): number {
  let next = left;
  if (next + tooltipWidth + VIEWPORT_PADDING > window.innerWidth) {
    next = window.innerWidth - tooltipWidth - VIEWPORT_PADDING;
  }
  if (next < VIEWPORT_PADDING) {
    next = VIEWPORT_PADDING;
  }
  return next;
}

function clampVertical(top: number, tooltipHeight: number): number {
  let next = top;
  if (next + tooltipHeight + VIEWPORT_PADDING > window.innerHeight) {
    next = window.innerHeight - tooltipHeight - VIEWPORT_PADDING;
  }
  if (next < VIEWPORT_PADDING) {
    next = VIEWPORT_PADDING;
  }
  return next;
}

function clampPosition(
  top: number,
  left: number,
  tooltipWidth: number,
  tooltipHeight: number
): TooltipPosition {
  return {
    top: clampVertical(top, tooltipHeight),
    left: clampHorizontal(left, tooltipWidth),
  };
}

function alignTopVertically(targetRect: DOMRect, tooltipHeight: number): number {
  if (targetRect.height >= tooltipHeight) {
    return targetRect.top + (targetRect.height - tooltipHeight) / 2;
  }
  return targetRect.top;
}

function centerLeft(targetRect: DOMRect, tooltipWidth: number): number {
  return targetRect.left + (targetRect.width - tooltipWidth) / 2;
}

function overlapsTarget(
  top: number,
  left: number,
  tooltipWidth: number,
  tooltipHeight: number,
  targetRect: DOMRect,
  gap: number
): boolean {
  const tooltipRight = left + tooltipWidth;
  const tooltipBottom = top + tooltipHeight;
  const targetTop = targetRect.top - gap;
  const targetLeft = targetRect.left - gap;
  const targetRight = targetRect.right + gap;
  const targetBottom = targetRect.bottom + gap;

  return !(
    tooltipRight <= targetLeft ||
    left >= targetRight ||
    tooltipBottom <= targetTop ||
    top >= targetBottom
  );
}

function fitsViewport(
  top: number,
  left: number,
  tooltipWidth: number,
  tooltipHeight: number
): boolean {
  return (
    top >= VIEWPORT_PADDING &&
    left >= VIEWPORT_PADDING &&
    top + tooltipHeight <= window.innerHeight - VIEWPORT_PADDING &&
    left + tooltipWidth <= window.innerWidth - VIEWPORT_PADDING
  );
}

function buildCandidate(
  side: PlacementSide,
  targetRect: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number
): TooltipPosition {
  switch (side) {
    case 'bottom':
      return {
        top: targetRect.bottom + TOOLTIP_GAP,
        left: centerLeft(targetRect, tooltipWidth),
      };
    case 'right':
      return {
        top: alignTopVertically(targetRect, tooltipHeight),
        left: targetRect.right + TOOLTIP_GAP,
      };
    case 'left':
      return {
        top: alignTopVertically(targetRect, tooltipHeight),
        left: targetRect.left - tooltipWidth - TOOLTIP_GAP,
      };
    case 'top':
      return {
        top: targetRect.top - tooltipHeight - TOOLTIP_GAP,
        left: centerLeft(targetRect, tooltipWidth),
      };
  }
}

function isTargetNearBottom(targetRect: DOMRect): boolean {
  return targetRect.bottom > window.innerHeight * 0.55;
}

const PLACEMENT_ORDER: Record<TooltipPlacementPreference, PlacementSide[]> = {
  default: ['bottom', 'right', 'left', 'top'],
  modal: ['bottom', 'right', 'left', 'top'],
};

function resolvePlacementOrder(
  preference: TooltipPlacementPreference,
  targetRect: DOMRect
): PlacementSide[] {
  if (preference === 'modal' && isCompactViewport()) {
    if (isTargetNearBottom(targetRect)) {
      return ['top', 'bottom'];
    }
    return ['bottom', 'top'];
  }
  return PLACEMENT_ORDER[preference];
}
export function computeTooltipPosition(
  targetRect: DOMRect | null,
  tooltipHeight = ONBOARDING_TOOLTIP_HEIGHT,
  options?: ComputeTooltipPositionOptions
): TooltipPosition {
  if (!targetRect) {
    const tooltipWidth = getTooltipWidth();
    return {
      top: Math.max(VIEWPORT_PADDING, (window.innerHeight - tooltipHeight) / 2),
      left: Math.max(VIEWPORT_PADDING, (window.innerWidth - tooltipWidth) / 2),
    };
  }

  const tooltipWidth = getTooltipWidth();
  const preference = options?.placementPreference ?? 'default';
  const order = resolvePlacementOrder(preference, targetRect);
  for (const side of order) {
    const raw = buildCandidate(side, targetRect, tooltipWidth, tooltipHeight);
    const clamped = clampPosition(raw.top, raw.left, tooltipWidth, tooltipHeight);

    if (
      !overlapsTarget(
        clamped.top,
        clamped.left,
        tooltipWidth,
        tooltipHeight,
        targetRect,
        TOOLTIP_GAP
      ) &&
      fitsViewport(clamped.top, clamped.left, tooltipWidth, tooltipHeight)
    ) {
      return clamped;
    }
  }

  // Fallback: abaixo do alvo, alinhado à direita da viewport se necessário (evita cobrir o topo)
  const belowTarget = clampPosition(
    targetRect.bottom + TOOLTIP_GAP,
    centerLeft(targetRect, tooltipWidth),
    tooltipWidth,
    tooltipHeight
  );

  if (
    !overlapsTarget(
      belowTarget.top,
      belowTarget.left,
      tooltipWidth,
      tooltipHeight,
      targetRect,
      TOOLTIP_GAP
    )
  ) {
    return belowTarget;
  }

  const rightOfTarget = clampPosition(
    alignTopVertically(targetRect, tooltipHeight),
    targetRect.right + TOOLTIP_GAP,
    tooltipWidth,
    tooltipHeight
  );

  if (
    !overlapsTarget(
      rightOfTarget.top,
      rightOfTarget.left,
      tooltipWidth,
      tooltipHeight,
      targetRect,
      TOOLTIP_GAP
    )
  ) {
    return rightOfTarget;
  }

  // Último recurso em mobile: acima do alvo (ex.: botão Continuar no rodapé)
  if (isCompactViewport() && isTargetNearBottom(targetRect)) {
    const aboveTarget = clampPosition(
      targetRect.top - tooltipHeight - TOOLTIP_GAP,
      centerLeft(targetRect, tooltipWidth),
      tooltipWidth,
      tooltipHeight
    );
    if (
      !overlapsTarget(
        aboveTarget.top,
        aboveTarget.left,
        tooltipWidth,
        tooltipHeight,
        targetRect,
        TOOLTIP_GAP
      )
    ) {
      return aboveTarget;
    }
  }

  // Último recurso: canto inferior da viewport, sem sobrepor o alvo
  const bottomViewport = clampPosition(
    window.innerHeight - tooltipHeight - VIEWPORT_PADDING,
    clampHorizontal(centerLeft(targetRect, tooltipWidth), tooltipWidth),
    tooltipWidth,
    tooltipHeight
  );

  if (
    !overlapsTarget(
      bottomViewport.top,
      bottomViewport.left,
      tooltipWidth,
      tooltipHeight,
      targetRect,
      TOOLTIP_GAP
    )
  ) {
    return bottomViewport;
  }

  return rightOfTarget;
}
